import React, { useState, useRef, useEffect, useContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaStar } from 'react-icons/fa';
import CategoryModal from '../elements/CategoryModal';
import { BsArrowUpCircle } from 'react-icons/bs';
import Context from '../Context';
import { getRestaurantThunk, getHoursThunk } from '../store/restaurant';
import { filterCategoriesByTime } from '../lib/filterCategories';

export default function Menu() {
  const dispatch = useDispatch();
  const { restaurantId } = useContext(Context);
  const storeRestaurant = useSelector((state) => state.restaurants.restaurant);
  const hours = useSelector((state) => state.restaurants?.hours);
  const restaurant = useMemo(
    () => filterCategoriesByTime(storeRestaurant, hours),
    [storeRestaurant, hours]
  );
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [activeCategory, setActiveCategory] = useState(0);

  useEffect(() => {
    dispatch(getRestaurantThunk(restaurantId));
    dispatch(getHoursThunk(restaurantId));
  }, [dispatch, restaurantId]);

  const categories = restaurant?.Categories || [];

  const handleCategoryClick = (categoryId) => {
    setActiveCategory(categoryId);
    setCategoryModalVisible(false);
    const categoryOffset = getCategoryOffset(categoryId);
    if (categoryOffset) {
      window.scrollTo({ top: categoryOffset, behavior: 'smooth' });
    }
  };

  const getCategoryOffset = (categoryId) => {
    const categoryElement = document.getElementById(`category-${categoryId}`);
    if (categoryElement) {
      const rect = categoryElement.getBoundingClientRect();
      return rect.top + window.scrollY - 200;
    }
    return 0;
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 250;
      (restaurant?.Categories || []).forEach((category, index) => {
        const categoryElement = document.getElementById(`category-${index}`);
        if (categoryElement) {
          const offsetTop = categoryElement.offsetTop;
          const offsetHeight = categoryElement.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveCategory(index);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [restaurant?.Categories]);

  if (!storeRestaurant?.Categories?.length) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '50vh', marginTop: '135px' }}
      >
        <div className="spinner-border text-danger" role="status">
          <span className="visually-hidden">Loading menu...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="container my-5">
        <div className="text-center mb-5">
          <p className="text-pink fw-bold" style={{ marginTop: '150px' }}>Our Menu</p>
        </div>

        {categories.map((category, index) => (
          <div key={category.id} className="mb-5" id={`category-${index}`}>
            <h3 className="text-center text-pink fw-bold mb-4">{category.name}</h3>
            <div className="grid-container">
              {category.Items.map((item) => (
                <div className="grid-item" key={item.id}>
                  <div className="dish-card shadow-sm rounded">
                    <img
                      src={item.image_url || 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcScO99JkPvdfSyT3CbMrYsKBpRuXOACVR2cP9F6DBcP9B6nQ9oszX_18T-2oNv6Gyxwyhk&usqp=CAU'}
                      alt={item.name}
                      className="img-fluid rounded-top dish-image"
                    />
                    <div className="text-center">
                      <h5 className="fw-bold mb-1">{item.name}</h5>
                      <div className="d-flex align-items-center justify-content-between">
                        <FaStar className="text-pink" />
                        <span className="fw-bold">${item.price}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <CategoryModal show={categoryModalVisible} onClose={() => setCategoryModalVisible(false)}>
          <div className="category-modal-content">
            {categories.map((category, index) => (
              <div
                key={category.id}
                className={`category-modal-item ${activeCategory === index ? 'active' : ''}`}
                onClick={() => handleCategoryClick(index)}
              >
                {category.name}
              </div>
            ))}
          </div>
        </CategoryModal>

        <button
          onClick={() => setCategoryModalVisible(true)}
          className="category-button"
        >
          {categories[activeCategory]?.name || "Categories"}
          <BsArrowUpCircle />
        </button>

        <style>{`
          .category-button {
            position: sticky;
            bottom: 0;
            width: 100%;
            padding: 12px;
            font-size: 18px;
            color: white;
            background-color: red;
            border: none;
            border-radius: 10px 10px 0 0;
            box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.3);
            text-align: center;
            cursor: pointer;
            transition: background-color 0.3s ease;
          }

          .grid-container {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 16px;
          }

          .grid-item {
            display: flex;
            flex-direction: column;
            align-items: center;
          }

          .dish-card {
            display: flex;
            flex-direction: column;
            align-items: center;
            height: 100%;
            width: 100%;
            max-height: 300px;
          }

          .dish-image {
            height: 150px;
            object-fit: cover;
            width: 100%;
          }

          @media (max-width: 576px) {
            .grid-container {
              grid-template-columns: repeat(2, 1fr);
            }
          }
        `}</style>
      </div>
    </div>
  );
}
